/**
 * Parsing for the agent's approval_summary.key_findings.
 *
 * Every validation agent emits a key finding as one line prefixed with an
 * outcome glyph: "✅ Eligible Services: <reason>". The prefix is the outcome
 * and the rest is "<check name>: <reason>", so the reviewer surfaces render a
 * per-check status icon rather than a wall of text.
 *
 * MAP/WMP/IW only ever emit ✅ and ❌. MMP/VMP has a seven-word outcome
 * vocabulary (rules/summary.py) and writes six distinct glyphs, which is why
 * the mapping is a table instead of two branches.
 */

/**
 * Cloudscape StatusIndicator type per outcome glyph, keyed on the BASE code
 * point: ⚠️ and ℹ️ are emitted with a trailing U+FE0F variation selector while
 * ✅/❌/❓/➖ are not, so keying on the full grapheme would miss half of them
 * (and slicing a fixed number of code units off the front would leave the
 * selector glued to the check name).
 */
const KEY_FINDING_STATUS = {
  '✅': 'success',
  '❌': 'error',
  '⚠': 'warning',       // needs_attention / out_of_scope
  '❓': 'pending',       // not_checked on rows written before 2026-10-01; the agent now prints ⚠️
  'ℹ': 'info',          // nothing_to_judge — a conclusion, not a gap
  '➖': 'info',          // does_not_apply
};

/**
 * True when the line already starts with an outcome glyph the agent wrote.
 *
 * Callers that guess an icon from the finding's wording must ask this first,
 * or they prepend a second glyph to a line that already had one — and guess a
 * severity the agent did not state (a `➖ does_not_apply` row shown as ⚠️).
 *
 * @param {string|null|undefined} finding
 * @returns {boolean}
 */
export function hasOutcomeGlyph(finding) {
  const [glyph] = Array.from(String(finding ?? '').trim());
  return Boolean(glyph) && Object.prototype.hasOwnProperty.call(KEY_FINDING_STATUS, glyph);
}

/**
 * Parse one key-finding line into a check row.
 *
 * An unrecognized (or absent) prefix yields status 'info' with the line kept
 * whole, so a new glyph degrades to a readable row instead of losing its first
 * character.
 *
 * @param {string|null|undefined} finding
 * @returns {{status: string, name: string, reason: string}}
 */
export function parseKeyFinding(finding) {
  const raw = String(finding ?? '').trim();
  const [glyph] = Array.from(raw);
  const status = KEY_FINDING_STATUS[glyph] || 'info';
  const rest = KEY_FINDING_STATUS[glyph]
    ? raw.slice(glyph.length).replace(/^\uFE0F/, '').trim()
    : raw;
  const sep = rest.indexOf(':');
  if (sep === -1) return { status, name: rest, reason: '' };
  return { status, name: rest.slice(0, sep).trim(), reason: rest.slice(sep + 1).trim() };
}
